import { useState, useEffect, Fragment } from "react";
import {
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import {
  DecisionTree,
  DecisionTreeQuestion,
} from "../../types/api/decision-tree";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import HtmlContent from "../common/HtmlContent";

type TreeQuestionnaireProps = {
  decisionTree: DecisionTree;
};

const TreeQuestionnaire = ({ decisionTree }: TreeQuestionnaireProps) => {
  const [selectedAnswers, setSelectedAnswers] = useState<
    Record<number, number | undefined>
  >({});
  const [visibleQuestions, setVisibleQuestions] = useState<Set<number>>(
    new Set()
  );

  useEffect(() => {
    const firstQuestion = decisionTree.questions.find((q) => q.isFirst);
    if (firstQuestion) {
      setVisibleQuestions(new Set([firstQuestion.id]));
    }
  }, [decisionTree]);

  const handleAnswerChange = (questionId: number, answerId: number) => {
    setSelectedAnswers((prev) => {
      const newSelectedAnswers = { ...prev };
      const newVisibleQuestions = new Set(visibleQuestions);

      // Remove answers and visibility for questions following the current one
      const queue = [questionId];
      while (queue.length > 0) {
        const currentId = queue.shift();
        const currentQuestion = decisionTree.questions.find(
          (q) => q.id === currentId
        );

        if (currentQuestion) {
          currentQuestion.answers.forEach((answer) => {
            if (answer.nextQuestionId) {
              delete newSelectedAnswers[answer.nextQuestionId];
              newVisibleQuestions.delete(answer.nextQuestionId);
              queue.push(answer.nextQuestionId);
            }
          });
        }
      }

      // Determine the next question to show based on the selected answer
      const selectedQuestionAnswer = decisionTree.questions
        .find((q) => q.id === questionId)
        ?.answers.find((ans) => ans.id === answerId);

      if (selectedQuestionAnswer && selectedQuestionAnswer.nextQuestionId) {
        newVisibleQuestions.add(selectedQuestionAnswer.nextQuestionId);
      }

      setVisibleQuestions(newVisibleQuestions);

      return {
        ...newSelectedAnswers,
        [questionId]: answerId,
      };
    });
  };

  const resetForm = () => {
    const firstQuestion = decisionTree.questions.find((q) => q.isFirst);
    setSelectedAnswers({});
    setVisibleQuestions(
      firstQuestion ? new Set([firstQuestion.id]) : new Set()
    );
  };

  const getOrderedQuestions = () => {
    const orderedQuestions: DecisionTreeQuestion[] = [];
    const processedIds = new Set<number>();

    // Start with first question
    const firstQuestion = decisionTree.questions.find((q) => q.isFirst);
    if (!firstQuestion) return orderedQuestions;

    const queue = [firstQuestion];

    while (queue.length > 0) {
      const currentQuestion = queue.shift()!;

      // Skip if already processed
      if (processedIds.has(currentQuestion.id)) continue;

      // Add to ordered list if visible
      if (visibleQuestions.has(currentQuestion.id)) {
        orderedQuestions.push(currentQuestion);
        processedIds.add(currentQuestion.id);
      }

      // Find the selected answer for this question
      const selectedAnswerId = selectedAnswers[currentQuestion.id];
      if (selectedAnswerId) {
        const selectedAnswer = currentQuestion.answers.find(
          (a) => a.id === selectedAnswerId
        );

        // Add next question to queue if exists
        if (selectedAnswer?.nextQuestionId) {
          const nextQuestion = decisionTree.questions.find(
            (q) => q.id === selectedAnswer.nextQuestionId
          );
          if (nextQuestion) queue.push(nextQuestion);
        }
      }
    }

    return orderedQuestions;
  };

  return (
    <div className="tree-questionnaire-container">
      {getOrderedQuestions().map((question) => {
        const selectedAnswer = question.answers.find(
          (a) => a.id === selectedAnswers[question.id]
        );
        return (
          <Fragment key={question.id}>
            <TreeQuestion
              question={question}
              onAnswerChange={handleAnswerChange}
              selectedAnswerId={selectedAnswers[question.id]}
            />
            {selectedAnswer && !!selectedAnswer.comments?.length && (
              <Stack gap={1}>
                {selectedAnswer.comments.map((comment) => (
                  <Stack
                    key={comment.id}
                    bgcolor={"#0074D91A"}
                    borderRadius={2}
                    border={1}
                    borderColor={"#0074D9"}
                    flexDirection={"row"}
                    alignItems={"flex-start"}
                    p={1}
                    gap={1}
                  >
                    <InfoIcon
                      sx={{ color: "#0074D9", flexShrink: 0, marginTop: "2px" }}
                    />
                    <HtmlContent
                      content={comment.label}
                      className="tree-comment-content"
                    />
                  </Stack>
                ))}
              </Stack>
            )}
          </Fragment>
        );
      })}
      {Object.entries(selectedAnswers).map(([questionId, answerId]) => {
        const question = decisionTree.questions.find(
          (q) => q.id === parseInt(questionId, 10)
        );
        const answer = question?.answers.find((a) => a.id === answerId);
        if (answer && !answer.nextQuestionId && answer.prescription) {
          return (
            <div
              key={`prescription-${answer.id}`}
              className="prescription-container"
            >
              <h3 className="prescription-title">
                {decisionTree.treeType === "PRESCRIPTION"
                  ? "Prescription"
                  : "Administration"}
              </h3>
              <HtmlContent
                content={answer.prescription.label}
                className="tree-prescription-content"
              />
            </div>
          );
        }
        return null;
      })}
      <div className="button-container">
        <Button
          variant="outlined"
          startIcon={<RestartAltIcon />}
          sx={{ fontSize: "16px", fontWeight: 600 }}
          onClick={resetForm}
        >
          Recommencer
        </Button>
      </div>
    </div>
  );
};

type TreeQuestionProps = {
  question: DecisionTreeQuestion;
  onAnswerChange: (questionId: number, answerId: number) => void;
  selectedAnswerId?: number;
};

const TreeQuestion = ({
  question,
  onAnswerChange,
  selectedAnswerId,
}: TreeQuestionProps) => {
  return (
    <div className="tree-question-container">
      <HtmlContent content={question.label} className="tree-question-label" />
      <RadioGroup
        value={selectedAnswerId || ""}
        onChange={(e, value) =>
          onAnswerChange(question.id, parseInt(value, 10))
        }
      >
        {question.answers.map((answer) => (
          <Stack p={1} gap={2} key={answer.id}>
            <FormControlLabel
              value={answer.id.toString()}
              control={<Radio />}
              label={<HtmlContent content={answer.label} as="span" />}
            />
          </Stack>
        ))}
      </RadioGroup>
    </div>
  );
};

export default TreeQuestionnaire;
